import {
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type VisibilityState,
} from '@tanstack/react-table'
import type { UseQueryResult } from '@tanstack/react-query'
import { ChevronRight, Columns3 } from 'lucide-react'
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { Checkbox } from '@/components/ui/Checkbox'
import { ErrorState } from '@/components/ui/ErrorState'
import { Segmented } from '@/components/ui/Segmented'
import { TableSkeleton } from '@/components/ui/Skeleton'
import { toggleClass } from '@/components/ui/toggle'
import { cn } from '@/lib/cn'
import { useDensity, type Density } from '@/lib/density'

/**
 * What a column holds. The type drives the layout (design §6.6, WP-14), so every list in the
 * admin reads the same way:
 * - `money`, `number`, `percent` — right-aligned, tabular figures, never wrapped (values come
 *   formatted by lib/format);
 * - `date` — never wrapped, caption ink-3;
 * - `id` — a `<Code>` chip, never wrapped; `url` — may break anywhere (UUIDs, addresses);
 * - `name` — at least 200 px, two lines at most;
 * - `status` — a StatusBadge, never wrapped; `actions` — right-aligned buttons;
 * - `text` — everything else.
 */
export type ColumnType = 'text' | 'name' | 'money' | 'number' | 'percent' | 'date' | 'id' | 'url' | 'status' | 'actions'

/**
 * Where a column goes on a phone (below `md` a row becomes a list item, WP-15):
 * line 1 — `title` on the left, `amount` on the right (tabular); line 2 — `status` and `meta`
 * (caption ink-3) as separate elements, never a «·» string; then `field`s — an editable cell
 * with its header as the visible caption («Коэффициент [1,6]»: a phone has no header row); last
 * `actions`. `hidden` columns live in the drawer.
 */
export type MobileSlot = 'title' | 'amount' | 'status' | 'meta' | 'field' | 'actions' | 'hidden'

export interface DataColumn<T> {
  id: string
  /** sentence case, no unit repeated in the cells («Сумма, USDT»); '' for actions */
  header: string
  /** the unit the header names («USDT» of «Сумма, USDT»). The phone list has no header, so
   *  there the unit follows the number («1 019,00 USDT», §6.6): a bare amount never stands
   *  alone on a screen where money leaves. */
  unit?: string
  type?: ColumnType
  cell: (row: T) => ReactNode
  /** a tooltip for the cell (the exact amount behind a rounded one) */
  title?: (row: T) => string | undefined
  mobile?: MobileSlot
  /** a `field` pair whose value is long («48 000,00 из 60 000,00»): on a phone the value goes
   *  under its caption, right-aligned, instead of squeezing the caption into a narrow column */
  stackOnPhone?: boolean
  /** the «Колонки» menu may hide it (every column but the first, by default) */
  hideable?: boolean
  /** hidden until the operator turns it on in «Колонки» */
  defaultHidden?: boolean
  /** extra classes for the cells (a tone: `text-ink-1`) */
  className?: string
  /** the cells show an exact amount with all its decimals on purpose (`fmtMoneyExact`, a
   *  crypto wallet): marked `data-exact` (design §6.6 — only the integer part must be grouped) */
  exact?: boolean
}

const NUMERIC: ReadonlySet<ColumnType> = new Set(['money', 'number', 'percent'])

/** A click on a control inside a row (an action button, a copyable `Code`, a «⋯» menu, a
 *  link) is that control's, not «open the row»: «Одобрить» must not also open the drawer. */
function fromControl(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('button, a, input, select, textarea, label, [role="menu"]') !== null
}

function cellClass(type: ColumnType): string {
  switch (type) {
    case 'money':
    case 'number':
    case 'percent':
      return 'tnum whitespace-nowrap text-right text-ink-1'
    case 'date':
      return 'tnum whitespace-nowrap text-caption text-ink-3'
    case 'id':
    case 'status':
      return 'whitespace-nowrap'
    case 'url':
      return 'min-w-[12rem] break-all text-ink-2'
    case 'name':
      return 'min-w-[200px] text-ink-1'
    case 'actions':
      return 'whitespace-nowrap px-2 text-right'
    default:
      return 'text-ink-2'
  }
}

/** A pair's label on a phone: the unit after the comma stays with its word («Эффективно, %»
 *  wrapped at 320 px and left «%» alone on the second line). */
function pairLabel(header: string): string {
  return header.replace(/, (?=\S{1,5}$)/u, ',\u00a0')
}

/** The value of a `field` pair on a phone: a formatted number (a string in a numeric column) is
 *  tabular and right-aligned, never wrapped (rule 20, WP-25: the fee tables' «Выручка, ₽ —
 *  120 464,00»); a control or a badge (an editable «Коэффициент») keeps its own look. */
function fieldValueClass<T>(c: DataColumn<T>, value: ReactNode): string {
  const text = typeof value === 'string' || typeof value === 'number'
  return text && NUMERIC.has(c.type ?? 'text')
    ? cn('tnum min-w-0 justify-self-end whitespace-nowrap text-right text-body text-ink-1', c.className)
    : 'min-w-0 justify-self-end'
}

/** Default phone slots: the first column is the title, the first amount the amount, the
 *  first status the status, dates the meta line, actions their own line; the rest hide. */
function mobileSlots<T>(columns: readonly DataColumn<T>[]): Map<string, MobileSlot> {
  const out = new Map<string, MobileSlot>()
  let title = false
  let amount = false
  let status = false
  for (const c of columns) {
    if (c.mobile) {
      out.set(c.id, c.mobile)
      if (c.mobile === 'title') title = true
      if (c.mobile === 'amount') amount = true
      if (c.mobile === 'status') status = true
    }
  }
  for (const c of columns) {
    if (out.has(c.id)) continue
    const type = c.type ?? 'text'
    if (!title && type !== 'actions') {
      out.set(c.id, 'title')
      title = true
    } else if (!amount && NUMERIC.has(type)) {
      out.set(c.id, 'amount')
      amount = true
    } else if (!status && type === 'status') {
      out.set(c.id, 'status')
      status = true
    } else if (type === 'date') out.set(c.id, 'meta')
    else if (type === 'actions') out.set(c.id, 'actions')
    else out.set(c.id, 'hidden')
  }
  return out
}

function readHidden(scope: string | undefined, columns: readonly DataColumn<unknown>[]): VisibilityState {
  const defaults: VisibilityState = Object.fromEntries(columns.filter((c) => c.defaultHidden).map((c) => [c.id, false]))
  if (!scope) return defaults
  try {
    const raw = window.localStorage.getItem(`aivy-admin:columns:${scope}`)
    if (raw === null) return defaults
    const hidden = JSON.parse(raw) as unknown
    if (!Array.isArray(hidden)) return defaults
    return Object.fromEntries(columns.map((c) => [c.id, !hidden.includes(c.id)]))
  } catch {
    return defaults
  }
}

/**
 * The totals row of a report table («Итого по каналам»): a cell per column id, the label in the
 * first column. On a desktop it is the table's `<tfoot>`; on a phone the last list item — the
 * label and the total of the `amount` column, with its unit.
 */
export interface DataTotals {
  label: string
  cells: Partial<Record<string, ReactNode>>
}

export interface DataTableProps<T> {
  columns: readonly DataColumn<T>[]
  getRowId: (row: T) => string
  /** opens the row (its drawer): the row is clickable, ends with a chevron button */
  onRowClick?: (row: T) => void
  /** the chevron's name for a screen reader: «Открыть заявку 3f2a9c1e» */
  rowLabel?: (row: T) => string
  /** the row whose drawer is open: a 2 px cyan line on its left (design §6.4) */
  selectedId?: string | null
  /** remembers the density (52 / 40 px) and hidden columns per table; shows the density switch */
  scope?: string
  /** a «Колонки» menu (wide tables) */
  columnsMenu?: boolean
  /** the table's name for a screen reader («Заявки») */
  label: string
  /** controls on the left of the toolbar (a count, an export) */
  toolbar?: ReactNode
  /** a totals row under the rows (report tables); shown only when there are rows */
  totals?: DataTotals
  className?: string
}

/**
 * The admin's list (WP-14, design §6.6) on @tanstack/react-table: a caption-size ink-2 header in
 * sentence case that sticks to the top of its own scroll box (the page's <main> scrolls sideways,
 * so sticky needs a box of its own, as on «Модели»), a sticky first column, a fade on the right
 * edge while more columns hide there, 52 or 40 px rows (`scope` remembers the choice), a
 * «Колонки» menu for wide tables, and a click on a row (or its chevron) that opens its drawer.
 * Below `md` every row is a two-line list item instead (WP-15): name and amount, then status and
 * time, so a 390 px phone shows what matters without scrolling sideways.
 */
export function DataTable<T>(props: DataTableProps<T> & { rows: readonly T[] }) {
  const { rows, ...rest } = props
  return (
    <TableFrame {...rest}>
      {(density, visibility) => <TableBody {...rest} rows={rows} density={density} visibility={visibility} />}
    </TableFrame>
  )
}

/**
 * `DataTable` fed by a query (WP-05 states inside the same frame): a skeleton shaped like the
 * table while loading, ErrorState with «Повторить» on failure (never «нет данных»), `empty`
 * when the list is empty.
 */
export function QueryTable<T, D>(
  props: DataTableProps<T> & {
    query: Pick<UseQueryResult<D>, 'data' | 'error' | 'isFetching' | 'refetch'>
    rows: (data: D) => readonly T[]
    errorTitle: string
    empty: ReactNode
  },
) {
  const { query, rows, errorTitle, empty, ...rest } = props
  return (
    <TableFrame {...rest}>
      {(density, visibility) => {
        if (query.data === undefined) {
          if (query.error) {
            return (
              <ErrorState
                title={errorTitle}
                error={query.error}
                retrying={query.isFetching}
                onRetry={() => void query.refetch()}
              />
            )
          }
          return <TableSkeleton cols={Math.min(rest.columns.length, 6)} />
        }
        const list = rows(query.data)
        if (list.length === 0) return empty
        return <TableBody {...rest} rows={list} density={density} visibility={visibility} />
      }}
    </TableFrame>
  )
}

/** The toolbar (density, columns) and the e1 frame around the body. */
function TableFrame<T>({
  columns,
  scope,
  columnsMenu,
  toolbar,
  label,
  className,
  children,
}: DataTableProps<T> & { children: (density: Density, visibility: VisibilityState) => ReactNode }) {
  const [density, setDensity] = useDensity(scope ?? 'default')
  const [visibility, setVisibility] = useState<VisibilityState>(() =>
    readHidden(scope, columns as readonly DataColumn<unknown>[]),
  )
  const setColumn = useCallback(
    (id: string, on: boolean) => {
      setVisibility((v) => {
        const next = { ...v, [id]: on }
        if (scope) {
          try {
            const hidden = Object.entries(next)
              .filter(([, shown]) => !shown)
              .map(([k]) => k)
            window.localStorage.setItem(`aivy-admin:columns:${scope}`, JSON.stringify(hidden))
          } catch {
            // storage blocked: the choice lives until the page is left
          }
        }
        return next
      })
    },
    [scope],
  )
  const showToolbar = toolbar !== undefined || scope !== undefined || columnsMenu
  return (
    <div className={cn('min-w-0', className)}>
      {showToolbar && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-3">{toolbar}</div>
          <div className="hidden items-center gap-2 md:flex print:hidden">
            {columnsMenu && (
              <ColumnsMenu columns={columns as readonly DataColumn<unknown>[]} visibility={visibility} onChange={setColumn} />
            )}
            {scope !== undefined && (
              <Segmented<Density>
                label={`Плотность строк: ${label}`}
                hideLabel
                size="sm"
                value={density}
                onChange={setDensity}
                options={[
                  { value: 'comfortable', label: 'Обычные строки' },
                  { value: 'compact', label: 'Компактные' },
                ]}
              />
            )}
          </div>
        </div>
      )}
      <div className="min-w-0 overflow-hidden rounded-md border border-hairline bg-surface-1">
        {children(density, visibility)}
      </div>
    </div>
  )
}

/** «Колонки (скрыта 1)», «(скрыты 2)», «(скрыто 5)»: the participle agrees with «колонка». */
function hiddenWord(n: number): string {
  const m10 = n % 10
  const m100 = n % 100
  if (m10 === 1 && m100 !== 11) return 'скрыта'
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'скрыты'
  return 'скрыто'
}

function ColumnsMenu({
  columns,
  visibility,
  onChange,
}: {
  columns: readonly DataColumn<unknown>[]
  visibility: VisibilityState
  onChange: (id: string, on: boolean) => void
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const id = useId()
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])
  const hideable = columns.filter((c, i) => i > 0 && c.header !== '' && c.hideable !== false)
  const hidden = hideable.filter((c) => visibility[c.id] === false).length
  return (
    <div ref={box} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        className={toggleClass(open, 'sm')}
        onClick={() => setOpen((o) => !o)}
      >
        <Columns3 aria-hidden size={14} strokeWidth={1.75} />
        Колонки
        {hidden > 0 && <span className="tnum font-normal">({hiddenWord(hidden)} {hidden})</span>}
      </button>
      {open && (
        <div
          id={id}
          role="group"
          aria-label="Показать колонки"
          className="absolute right-0 top-full z-30 mt-2 flex w-64 flex-col gap-1 rounded-sm border border-hairline-strong bg-surface-2 p-3 shadow-e2"
        >
          {hideable.map((c) => (
            <Checkbox
              key={c.id}
              label={c.header}
              checked={visibility[c.id] !== false}
              onChange={(on) => onChange(c.id, on)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function TableBody<T>({
  columns,
  rows,
  getRowId,
  onRowClick,
  rowLabel,
  selectedId,
  label,
  totals,
  density,
  visibility,
}: DataTableProps<T> & { rows: readonly T[]; density: Density; visibility: VisibilityState }) {
  const defs = useMemo<ColumnDef<T>[]>(
    () => columns.map((c) => ({ id: c.id, header: c.header, cell: (ctx) => c.cell(ctx.row.original), meta: c })),
    [columns],
  )
  const table = useReactTable<T>({
    data: rows as T[],
    columns: defs,
    getRowId: (r) => getRowId(r),
    getCoreRowModel: getCoreRowModel(),
    state: { columnVisibility: visibility },
  })
  const shown = table.getVisibleLeafColumns().map((c) => c.columnDef.meta as DataColumn<T>)
  const slots = useMemo(() => mobileSlots(columns), [columns])
  const ofSlot = (slot: MobileSlot) => columns.filter((c) => slots.get(c.id) === slot)

  // The right-edge fade: on while columns hide past the right edge.
  const scroller = useRef<HTMLDivElement>(null)
  const [more, setMore] = useState(false)
  const measure = useCallback(() => {
    const el = scroller.current
    if (!el) return
    setMore(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])
  useEffect(() => {
    measure()
    const el = scroller.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [measure, rows, visibility])

  const rowH = density === 'compact' ? 'h-10' : 'h-13'
  const clickable = onRowClick !== undefined

  return (
    <>
      {/* md and up: the table */}
      <div
        ref={scroller}
        onScroll={measure}
        className={cn('hidden max-h-pane overflow-auto md:block', more && 'fade-end')}
      >
        <table className="w-full border-separate border-spacing-0 text-body" aria-label={label}>
          <thead>
            <tr>
              {shown.map((c, i) => {
                const type = c.type ?? 'text'
                return (
                  <th
                    key={c.id}
                    scope="col"
                    className={cn(
                      'sticky top-0 z-10 h-10 whitespace-nowrap border-b border-hairline bg-surface-1 px-3 text-left text-caption font-medium text-ink-2',
                      NUMERIC.has(type) && 'text-right',
                      type === 'actions' && 'text-right',
                      i === 0 && 'left-0 z-20',
                    )}
                  >
                    {c.header || <span className="sr-only">Действия</span>}
                  </th>
                )
              })}
              {clickable && (
                <th scope="col" className="sticky top-0 z-10 w-9 border-b border-hairline bg-surface-1 print:hidden">
                  <span className="sr-only">Открыть</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {table.getRowModel().rows.map((r) => {
              const row = r.original
              const selected = selectedId !== undefined && selectedId !== null && r.id === selectedId
              return (
                <tr
                  key={r.id}
                  onClick={clickable ? (e) => !fromControl(e.target) && onRowClick(row) : undefined}
                  aria-selected={selected || undefined}
                  className={cn('group', rowH, clickable && 'cursor-pointer')}
                >
                  {shown.map((c, i) => (
                    <td
                      key={c.id}
                      title={c.title?.(row)}
                      data-exact={c.exact || undefined}
                      className={cn(
                        'border-b border-hairline px-3 py-2 align-middle group-last:border-b-0 group-hover:bg-surface-3',
                        cellClass(c.type ?? 'text'),
                        c.className,
                        i === 0 && 'sticky left-0 z-[1] bg-surface-1',
                        i === 0 && selected && 'shadow-[inset_2px_0_0_var(--cyan)]',
                      )}
                    >
                      {c.type === 'name' ? <span className="line-clamp-2">{c.cell(row)}</span> : c.cell(row)}
                    </td>
                  ))}
                  {clickable && (
                    <td className="border-b border-hairline pl-0 pr-1 text-right group-last:border-b-0 group-hover:bg-surface-3 print:hidden">
                      <button
                        type="button"
                        aria-label={rowLabel?.(row) ?? 'Открыть'}
                        onClick={(e) => {
                          e.stopPropagation()
                          onRowClick(row)
                        }}
                        className="inline-flex size-8 items-center justify-center rounded-sm text-ink-3 hover:bg-surface-2 hover:text-ink-1"
                      >
                        <ChevronRight aria-hidden size={16} strokeWidth={1.75} />
                      </button>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
          {totals && rows.length > 0 && (
            <tfoot>
              <tr className={rowH}>
                {shown.map((c, i) => (
                  <td
                    key={c.id}
                    className={cn(
                      'border-t border-hairline-strong px-3 py-2 align-middle font-medium',
                      cellClass(c.type ?? 'text'),
                      'text-ink-1',
                      i === 0 && 'sticky left-0 z-[1] bg-surface-1',
                    )}
                  >
                    {totals.cells[c.id] ?? (i === 0 ? totals.label : null)}
                  </td>
                ))}
                {clickable && <td className="border-t border-hairline-strong print:hidden" />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* below md: a list item per row */}
      <ul className="divide-y divide-hairline md:hidden" aria-label={label}>
        {table.getRowModel().rows.map((r) => {
          const row = r.original
          const selected = selectedId !== undefined && selectedId !== null && r.id === selectedId
          const title = ofSlot('title')
          const amount = ofSlot('amount')
          const line2 = columns.filter((c) => slots.get(c.id) === 'status' || slots.get(c.id) === 'meta')
          const fields = ofSlot('field')
          const actions = ofSlot('actions')
          const chevron = clickable ? (
            <button
              type="button"
              aria-label={rowLabel?.(row) ?? 'Открыть'}
              onClick={(e) => {
                e.stopPropagation()
                onRowClick(row)
              }}
              className="-my-2 -mr-2 inline-flex size-11 shrink-0 items-center justify-center rounded-sm text-ink-3 print:hidden"
            >
              <ChevronRight aria-hidden size={16} strokeWidth={1.75} />
            </button>
          ) : null
          return (
            <li
              key={r.id}
              onClick={clickable ? (e) => !fromControl(e.target) && onRowClick(row) : undefined}
              className={cn(
                'flex flex-col gap-1.5 px-4 py-3',
                clickable && 'cursor-pointer active:bg-surface-3',
                selected && 'shadow-[inset_2px_0_0_var(--cyan)]',
              )}
            >
              <div className="flex min-w-0 items-start justify-between gap-3">
                <div className="min-w-0 text-body text-ink-1 [overflow-wrap:anywhere]">
                  {title.map((c) => (
                    <span key={c.id} className="line-clamp-2">
                      {c.cell(row)}
                    </span>
                  ))}
                </div>
                {amount.map((c) => (
                  <span
                    key={c.id}
                    className="tnum shrink-0 whitespace-nowrap text-right text-body text-ink-1"
                    title={c.title?.(row)}
                    data-exact={c.exact || undefined}
                  >
                    {c.cell(row)}
                    {c.unit && `\u00a0${c.unit}`}
                  </span>
                ))}
                {/* No second line (no status, no meta): the chevron ends the first one instead of
                    taking a line of its own (wave-4 review: «Поддержка → Открытые» on a phone). */}
                {line2.length === 0 && chevron}
              </div>
              {line2.length > 0 && (
                <div className="flex min-w-0 items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                    {line2.map((c) => (
                      <span
                        key={c.id}
                        className={slots.get(c.id) === 'meta' ? 'whitespace-nowrap text-caption text-ink-3' : 'min-w-0'}
                      >
                        {c.cell(row)}
                      </span>
                    ))}
                  </div>
                  {chevron}
                </div>
              )}
              {fields.length > 0 && (
                <dl className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 pt-1">
                  {fields.map((c) => {
                    const value = c.cell(row)
                    return (
                      <div key={c.id} className="contents">
                        <dt className={cn('text-caption text-ink-2', c.stackOnPhone && 'col-span-2')}>{pairLabel(c.header)}</dt>
                        <dd
                          className={cn(fieldValueClass(c, value), c.stackOnPhone && 'col-span-2 -mt-1.5')}
                          title={c.title?.(row)}
                          data-exact={c.exact || undefined}
                        >
                          {value}
                        </dd>
                      </div>
                    )
                  })}
                </dl>
              )}
              {actions.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
                  {actions.map((c) => (
                    <span key={c.id} className="contents">
                      {c.cell(row)}
                    </span>
                  ))}
                </div>
              )}
            </li>
          )
        })}
        {totals && rows.length > 0 && (
          <li className="flex flex-col gap-1.5 px-4 py-3 font-medium">
            <div className="flex min-w-0 items-start justify-between gap-3">
              <span className="min-w-0 text-body text-ink-1">{totals.label}</span>
              {ofSlot('amount')
                .filter((c) => totals.cells[c.id] !== undefined)
                .map((c) => (
                  <span key={c.id} className="tnum shrink-0 whitespace-nowrap text-right text-body text-ink-1">
                    {totals.cells[c.id]}
                    {c.unit && `\u00a0${c.unit}`}
                  </span>
                ))}
            </div>
            {/* The pairs of the rows above have their totals too (WP-25: «Выручка, ₽», «Чистыми, ₽»
                of the fee tables), in the same label–value grid. */}
            {ofSlot('field').some((c) => totals.cells[c.id] !== undefined) && (
              <dl className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 pt-1">
                {ofSlot('field')
                  .filter((c) => totals.cells[c.id] !== undefined)
                  .map((c) => (
                    <div key={c.id} className="contents">
                      <dt className="text-caption font-normal text-ink-2">{pairLabel(c.header)}</dt>
                      <dd className={fieldValueClass(c, totals.cells[c.id])}>{totals.cells[c.id]}</dd>
                    </div>
                  ))}
              </dl>
            )}
          </li>
        )}
      </ul>
    </>
  )
}
